import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { ClaService } from "../ClaService.ts";
import { useClaService } from "./useClaService.ts";
import { ClaState } from "../ClaState.ts";
import { useFeatureFlagValue } from "../../../../config/FeatureFlagManager/hooks.ts";

export const useAppState = <T>(claService: ClaService, selector: () => T): T =>
  useSyncExternalStore(claService.subscribe, selector);

export const useChatHistory = () => {
  const claService = useClaService();
  const enableDeepResearchAgent = useFeatureFlagValue(
    "enableDeepResearchAgent",
  );
  const enableCloudRun = useFeatureFlagValue("enableCloudRun");
  const enableProductTestingBusinessDomain = useFeatureFlagValue(
    "enableProductTestingBusinessDomain",
  );

  useEffect(() => {
    const agent = enableDeepResearchAgent ? "DRA" : "DSA";
    const agentName = enableCloudRun
      ? agent === "DRA"
        ? "deep_research_agent"
        : "data_science_agent"
      : agent;
    const businessDomain = enableProductTestingBusinessDomain
      ? "InnovationProductTesting"
      : "InnovationConceptTesting";

    claService.fetchChatHistory({
      agent: agentName,
      businessDomain: businessDomain,
    });
  }, [
    claService,
    enableDeepResearchAgent,
    enableCloudRun,
    enableProductTestingBusinessDomain,
  ]);

  return useAppState(claService, () => claService.getState().chats);
};

export const useChatMessages = (
  chatId: string,
): {
  messages: ClaState["chats"][number]["messages"];
  loading: boolean;
} => {
  const claService = useClaService();
  const chatHistory = useChatHistory();
  const [loading, setLoading] = useState(true);
  const chatBoxSending = useIsChatBoxSending();
  useEffect(() => {
    if (chatBoxSending) return;
    setLoading(true);
    claService.fetchChatMessages(chatId).then(() => setLoading(false));
  }, [claService, chatId, chatBoxSending]);

  return useMemo(
    () => ({
      messages: chatHistory.find((chat) => chat.id === chatId)?.messages || [],
      loading,
    }),
    [chatHistory, chatId, loading],
  );
};

export const useIsChatBoxSending = () => {
  const claService = useClaService();
  return useAppState(claService, () => claService.getState().chatBoxSending);
};
